import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Hexagon, ShieldCheck, AlertCircle, Receipt, Bot, ExternalLink, LogIn } from "lucide-react";
import { toast } from "sonner";
import { embedApi, agentsApi } from "../lib/ledgerApi";
import { useAuth } from "../contexts/AuthContext";

/**
 * Public payment page rendered when an Embed Agent Pay URL is opened.
 * Anyone can see the invoice card. Logged-in business users get a
 * "Pay with agent X" picker for their compatible agents. Unauth → CTA.
 */
export default function PublicEmbedPay() {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [compat, setCompat] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    let alive = true;
    embedApi
      .resolve(token)
      .then((r) => alive && setInvoice(r))
      .catch((e) =>
        alive && setError(e?.response?.data?.detail || { code: "ERR", message: e.message })
      );
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    if (!user || !invoice || invoice.status !== "active") return;
    let alive = true;
    embedApi
      .compatibleAgents(token)
      .then((r) => alive && setCompat(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user, invoice, token]);

  const onPay = async (agentId) => {
    if (paying) return;
    setPaying(true);
    try {
      // Mint a fresh API key for the agent so we have a Bearer to call /agent/pay.
      // (We use the agent's own key route — same business owns both.)
      const key = await agentsApi.mintKey(agentId);
      const apiKey = key.secret;
      const nonce = `embed-${token.slice(0, 18)}-${Date.now()}`;
      const valid_until = new Date(Date.now() + 60_000).toISOString();
      const body = {
        to_vpa: invoice.merchant.vpa,
        currency: invoice.currency,
        amount: invoice.max_amount,
        nonce,
        valid_until,
        description: invoice.description,
        category: invoice.category,
        embed_token: token,
      };
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(
          (data?.detail && (data.detail.message || data.detail.code)) ||
            `pay failed (${r.status})`
        );
      }
      if (data.status === "executed") {
        setPaid({
          receipt_id: data.receipt?.receipt_id,
          tx_id: data.tx_id || data.receipt?.tx_id,
          block_number: data.block_number || data.receipt?.block_number,
        });
        toast.success("Payment executed");
      } else if (data.status === "pending_approval") {
        toast.info("Payment queued for human approval");
        setPaid({ pending: true, intent_id: data.intent_id });
      } else if (data.status === "held") {
        toast.warning("Held for fraud review");
        setPaid({ held: true, intent_id: data.intent_id });
      } else {
        toast.success("Payment submitted");
        setPaid(data);
      }
    } catch (e) {
      toast.error(e.message || "payment failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="embed-pay-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
      </Link>

      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ x402 · Embed Pay</div>
      <h1 className="text-3xl tracking-tight font-medium flex items-center gap-3 mb-2">
        <Receipt className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
        Pay invoice
      </h1>

      {error && (
        <div className="mt-4 border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm" data-testid="embed-pay-error">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">{error.code || "error"}</div>
          {error.message || JSON.stringify(error)}
        </div>
      )}

      {invoice && (
        <>
          <InvoiceCard invoice={invoice} />

          {invoice.status !== "active" && (
            <div className="mt-4 border border-amber-500/40 bg-amber-500/10 p-4 rounded-sm text-sm" data-testid="embed-pay-status-warning">
              This invoice is <strong>{invoice.status}</strong>. It cannot be paid.
            </div>
          )}

          {invoice.status === "active" && paid && (
            <div className="mt-4 border border-status-ok/40 bg-status-ok/10 p-4 rounded-sm text-sm" data-testid="embed-pay-result">
              {paid.tx_id ? (
                <>
                  <div className="flex items-center gap-2 mb-2 font-medium text-status-ok">
                    <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Paid · committed by federation
                  </div>
                  <div className="font-mono text-xs">
                    block #{paid.block_number ?? "?"} ·{" "}
                    <Link to={`/tx/${paid.tx_id}`} className="underline" data-testid="embed-pay-tx-link">
                      view proof →
                    </Link>
                  </div>
                </>
              ) : paid.pending ? (
                <span>Held for human approval. Intent <code>{paid.intent_id}</code></span>
              ) : paid.held ? (
                <span>Held for fraud review. Intent <code>{paid.intent_id}</code></span>
              ) : (
                <pre className="text-[10px] overflow-auto">{JSON.stringify(paid, null, 2)}</pre>
              )}
            </div>
          )}

          {invoice.status === "active" && !paid && (
            <>
              {!user ? (
                <div className="mt-6 border border-border rounded-sm bg-card p-5 text-sm" data-testid="embed-pay-cta-login">
                  <div className="mb-3">Log in or sign up to pay this invoice with one of your AI agents.</div>
                  <div className="flex gap-2">
                    <button onClick={() => nav(`/login?next=/x402/${token}`)} className="px-3 py-1.5 border border-foreground bg-foreground text-background rounded-sm text-xs hover:opacity-90 inline-flex items-center gap-1" data-testid="embed-pay-login-btn">
                      <LogIn className="w-3 h-3" strokeWidth={1.5} /> Login
                    </button>
                    <button onClick={() => nav(`/signup?next=/x402/${token}`)} className="px-3 py-1.5 border border-border rounded-sm text-xs hover:border-foreground" data-testid="embed-pay-signup-btn">
                      Sign up
                    </button>
                  </div>
                </div>
              ) : (
                <CompatibleAgentList compat={compat} paying={paying} onPay={onPay} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function InvoiceCard({ invoice }) {
  return (
    <div className="border border-border rounded-sm bg-card p-5 md:p-6 mt-4" data-testid="embed-invoice-card">
      <div className="grid grid-cols-2 gap-4 text-sm font-mono">
        <KV label="To" value={invoice.merchant?.legal_name || invoice.merchant?.vpa} testid="embed-merchant-name" />
        <KV label="VPA" value={invoice.merchant?.vpa} mono />
        <KV label="Amount" value={`${invoice.max_amount} ${invoice.currency}`} mono testid="embed-amount" />
        <KV label="Status" value={invoice.status} />
        <KV label="Issued by" value={invoice.issued_by?.display_name || invoice.issued_by?.agent_did} testid="embed-issuer" />
        <KV label="Expires" value={invoice.expires_at?.slice(0, 19).replace("T", " ")} mono />
      </div>
      {invoice.description && (
        <div className="mt-4 border-t border-border pt-3 text-sm">{invoice.description}</div>
      )}
    </div>
  );
}

function KV({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div className={`break-all ${mono ? "" : "font-sans"}`}>{value || "—"}</div>
    </div>
  );
}

function CompatibleAgentList({ compat, paying, onPay }) {
  if (!compat) {
    return <div className="mt-4 text-sm text-muted-foreground">Checking which of your agents can pay…</div>;
  }
  return (
    <div className="mt-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Pay with one of your agents</div>
      {compat.compatible.length === 0 ? (
        <div className="border border-amber-500/40 bg-amber-500/10 p-4 rounded-sm text-sm" data-testid="embed-pay-no-compat">
          None of your agents can pay this invoice.
          {compat.incompatible.length > 0 && (
            <ul className="mt-2 text-xs list-disc list-inside text-muted-foreground space-y-1">
              {compat.incompatible.slice(0, 5).map((a) => (
                <li key={a.agent_id}>
                  <span className="font-medium text-foreground">{a.display_name}</span>:{" "}
                  {a.message || a.code}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 text-xs">
            <Link to="/app/developer" className="underline">Manage agents →</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="embed-compat-list">
          {compat.compatible.map((a) => (
            <button
              key={a.agent_id}
              onClick={() => onPay(a.agent_id)}
              disabled={paying}
              data-testid={`embed-pay-with-${a.display_name.replace(/\s+/g, "-")}`}
              className="w-full border border-border rounded-sm bg-card p-4 hover:border-foreground transition-colors text-left flex items-center justify-between gap-3 disabled:opacity-50"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.display_name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate">{a.did}</div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-widest font-mono px-2 py-1 border border-status-ok/40 bg-status-ok/10 text-status-ok rounded-sm">
                {paying ? "…" : "pay"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
